/**
 * Utilitário para logging controlado
 *
 * Este utilitário permite controlar os logs de debug no aplicativo:
 * - Em desenvolvimento: todos os logs são exibidos
 * - Em produção: logs são suprimidos, exceto erros críticos
 *
 * Também permite habilitar/desabilitar categorias específicas de logs
 */

// Configuração de quais categorias de log estão habilitadas
const enabledCategories = {
  api: true, // Logs de API e requisições
  chat: true, // Logs relacionados ao chat
  leads: false, // Logs relacionados aos leads
  auth: true, // Logs de autenticação
  ui: false, // Logs de interface
};

// Verifica se está em ambiente de desenvolvimento
const isDev = process.env.NODE_ENV === "development";

// Função para verificar se um log deve ser exibido
const shouldLog = (category: string): boolean => {
  if (!isDev) return false; // Em produção, não exibe logs
  if (
    !category ||
    !Object.prototype.hasOwnProperty.call(enabledCategories, category)
  )
    return true; // Sem categoria específica
  return enabledCategories[category as keyof typeof enabledCategories];
};

// Interface para o logger
interface Logger {
  log: (message: unknown, ...args: unknown[]) => void;
  info: (message: unknown, ...args: unknown[]) => void;
  warn: (message: unknown, ...args: unknown[]) => void;
  error: (message: unknown, ...args: unknown[]) => void;
  group: (label: string) => void;
  groupEnd: () => void;
}

// Cria um logger para uma categoria específica
export const createLogger = (category: string): Logger => {
  return {
    log: (message: unknown, ...args: unknown[]) => {
      if (shouldLog(category)) {
        console.log(`[${category}]`, message, ...args);
      }
    },
    info: (message: unknown, ...args: unknown[]) => {
      if (shouldLog(category)) {
        console.info(`[${category}]`, message, ...args);
      }
    },
    warn: (message: unknown, ...args: unknown[]) => {
      // Warnings são sempre exibidos em desenvolvimento
      if (isDev) {
        console.warn(`[${category}]`, message, ...args);
      }
    },
    error: (message: unknown, ...args: unknown[]) => {
      // Erros são sempre exibidos, mesmo em produção
      console.error(`[${category}]`, message, ...args);
    },
    group: (label: string) => {
      if (shouldLog(category)) {
        console.group(`[${category}] ${label}`);
      }
    },
    groupEnd: () => {
      if (shouldLog(category)) {
        console.groupEnd();
      }
    },
  };
};

// Loggers pré-configurados para uso comum
export const apiLogger = createLogger("api");
export const chatLogger = createLogger("chat");
export const leadsLogger = createLogger("leads");
export const authLogger = createLogger("auth");
export const uiLogger = createLogger("ui");

// Logger genérico para uso geral
export const logger = createLogger("app");

// Função para habilitar/desabilitar uma categoria de log
export const setLoggerEnabled = (
  category: keyof typeof enabledCategories,
  enabled: boolean
): void => {
  if (Object.prototype.hasOwnProperty.call(enabledCategories, category)) {
    enabledCategories[category] = enabled;
  }
};
